/*
 * Iter315 — Skill Ranges Editor (organiser-facing).
 *
 * Full CRUD over the festival's `skill_ranges` array. Each row is a
 * {name, min, max, colour_hex} tuple; server enforces min 2 rows and
 * validates on PUT. Local state edits, single PUT on save. Range
 * boundaries render inline; colour picker uses a native <input> so
 * organisers don't need to memorise hex codes.
 *
 * The organiser opens this from a "Skill Ranges" button we add in the
 * ops Quick Actions rail. Saving triggers a bulk recompute on the
 * backend (colour_band on every player row) so the OPS console + wall
 * + self-service dots flip immediately without a manual refresh.
 */
import { useEffect, useState } from "react";
import { X, Plus, Trash2, GripVertical } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_COLOURS = ["#CCFF00", "#66C2FF", "#FF4500", "#FFB020", "#B266FF", "#4CAF50"];

const emptyRow = (i) => ({
  id: `range-${i + 1}`,
  name: `Range ${i + 1}`,
  min: 0,
  max: 8,
  colour_hex: DEFAULT_COLOURS[i % DEFAULT_COLOURS.length],
});

// Preset starter packs so new organisers don't build brackets from
// scratch. Each preset supplies name / min / max / colour_hex tuples in
// the exact shape the editor expects. Colours draw from the same
// DEFAULT_COLOURS palette so downstream chips stay on-brand.
const PRESETS = {
  "dupr-3": {
    label: "3-band DUPR",
    hint: "Beginner / Intermediate / Advanced",
    ranges: [
      { name: "Beginner", min: 0, max: 3, colour_hex: "#4CAF50" },
      { name: "Intermediate", min: 3, max: 4, colour_hex: "#66C2FF" },
      { name: "Advanced", min: 4, max: 8, colour_hex: "#FF4500" },
    ],
  },
  "uk-5": {
    label: "5-band UK club",
    hint: "New · Improver · Club · Club+ · Competitive",
    ranges: [
      { name: "New", min: 0, max: 2.5, colour_hex: "#4CAF50" },
      { name: "Improver", min: 2.5, max: 3.25, colour_hex: "#CCFF00" },
      { name: "Club", min: 3.25, max: 3.75, colour_hex: "#66C2FF" },
      { name: "Club+", min: 3.75, max: 4.25, colour_hex: "#FFB020" },
      { name: "Competitive", min: 4.25, max: 8, colour_hex: "#FF4500" },
    ],
  },
  "junior-4": {
    label: "Junior 4-band",
    hint: "U8 · U10 · U12 · U14",
    ranges: [
      { name: "U8 Red", min: 0, max: 1.5, colour_hex: "#FF4500" },
      { name: "U10 Orange", min: 1.5, max: 2.5, colour_hex: "#FFB020" },
      { name: "U12 Green", min: 2.5, max: 3.5, colour_hex: "#4CAF50" },
      { name: "U14 Yellow", min: 3.5, max: 8, colour_hex: "#CCFF00" },
    ],
  },
};

const stampPreset = (preset) =>
  preset.ranges.map((r, i) => ({
    id: `range-${i + 1}`,
    name: r.name,
    min: r.min,
    max: r.max,
    colour_hex: r.colour_hex,
  }));

export default function SkillRangesModal({ festivalId, open, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/festival/${festivalId}/skill-ranges`);
        if (!cancelled) setRows(r.data.skill_ranges || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Couldn't load ranges");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, festivalId]);

  const patchRow = (idx, patch) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addRow = () => setRows((rs) => [...rs, emptyRow(rs.length)]);
  const removeRow = (idx) => setRows((rs) => rs.filter((_, i) => i !== idx));

  const applyPreset = (key) => {
    const preset = PRESETS[key];
    if (!preset) return;
    if (rows.length > 0) {
      const ok = window.confirm(
        `Replace your current ${rows.length} range${rows.length === 1 ? "" : "s"} with the "${preset.label}" preset?\n\nYou can still tweak names, ratings and colours before saving.`,
      );
      if (!ok) return;
    }
    setRows(stampPreset(preset));
  };

  const save = async () => {
    if (rows.length < 2) {
      toast.error("Add at least 2 ranges");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/festival/${festivalId}/skill-ranges`, { skill_ranges: rows });
      toast.success(`Ranges saved — ${r.data.players_updated} player rows recoloured`);
      onSaved?.(r.data.skill_ranges);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="skill-ranges-modal">
      <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-3xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#262626]">
          <div>
            <div className="label-mono text-[#CCFF00]">SKILL RANGES</div>
            <div className="text-lg font-bold text-white">Set the colours that group players together</div>
            <div className="text-[11px] text-[#A3A3A3] mt-1">
              Auto-assign only mixes players within the same range or one range either side.
              Minimum 2 ranges — no upper limit. DUPR wins over self-rating.
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="skill-ranges-close" className="p-1 text-[#525252] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-[#A3A3A3]">Loading…</div>
        ) : (
          <div className="p-4 space-y-2">
            {/* Preset picker — one-tap starter packs. Each button stamps a
                curated set of ranges into the editor (unsaved) so the
                organiser can tweak before hitting Save. */}
            <div className="border border-dashed border-[#262626] p-3" data-testid="skill-ranges-presets">
              <div className="flex items-center justify-between mb-2">
                <div className="label-mono text-[#66C2FF] text-[10px]">START FROM PRESET</div>
                <div className="text-[10px] text-[#525252]">One tap · editable after</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {Object.entries(PRESETS).map(([key, p]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => applyPreset(key)}
                    data-testid={`skill-ranges-preset-${key}`}
                    className="text-left px-3 py-2 border border-[#262626] hover:border-[#66C2FF] hover:bg-[#0A1A2A] transition-colors group"
                    title={`Load the ${p.label} preset`}
                  >
                    <div className="text-xs font-bold text-white group-hover:text-[#66C2FF]">{p.label}</div>
                    <div className="text-[10px] text-[#A3A3A3] mt-0.5">{p.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-[24px_1fr_80px_80px_44px_36px] gap-2 text-[10px] uppercase tracking-wider text-[#525252] px-1">
              <span />
              <span>Name</span>
              <span>Min rating</span>
              <span>Max rating</span>
              <span>Colour</span>
              <span />
            </div>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[24px_1fr_80px_80px_44px_36px] gap-2 items-center" data-testid={`skill-range-row-${i}`}>
                <GripVertical className="w-4 h-4 text-[#525252]" />
                <input
                  type="text"
                  value={r.name}
                  onChange={(e) => patchRow(i, { name: e.target.value })}
                  data-testid={`skill-range-name-${i}`}
                  className="bg-[#141414] border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]"
                  maxLength={60}
                />
                <input
                  type="number"
                  step="0.01"
                  value={r.min}
                  onChange={(e) => patchRow(i, { min: Number(e.target.value) })}
                  data-testid={`skill-range-min-${i}`}
                  className="bg-[#141414] border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]"
                />
                <input
                  type="number"
                  step="0.01"
                  value={r.max}
                  onChange={(e) => patchRow(i, { max: Number(e.target.value) })}
                  data-testid={`skill-range-max-${i}`}
                  className="bg-[#141414] border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]"
                />
                <input
                  type="color"
                  value={r.colour_hex}
                  onChange={(e) => patchRow(i, { colour_hex: e.target.value })}
                  data-testid={`skill-range-colour-${i}`}
                  className="w-full h-8 bg-transparent cursor-pointer border border-[#262626]"
                  title="Pick colour"
                />
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  disabled={rows.length <= 2}
                  data-testid={`skill-range-remove-${i}`}
                  className="p-1 text-[#525252] hover:text-[#FF4500] disabled:opacity-30 disabled:cursor-not-allowed"
                  title={rows.length <= 2 ? "Minimum 2 ranges" : "Remove this range"}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addRow}
              data-testid="skill-range-add"
              className="w-full mt-2 py-2 border border-dashed border-[#262626] text-xs uppercase tracking-widest text-[#A3A3A3] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center justify-center gap-1"
            >
              <Plus className="w-4 h-4" /> Add another range
            </button>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 p-4 border-t border-[#262626]">
          <button
            type="button"
            onClick={onClose}
            data-testid="skill-ranges-cancel"
            className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A3A3A3] hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || rows.length < 2}
            data-testid="skill-ranges-save"
            className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save ranges"}
          </button>
        </div>
      </div>
    </div>
  );
}
